import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type KeyboardEvent } from 'react';
import type { ProductLine, Speed } from '@popcart/core/v1';
import { Icon, Lockup, PopLoop, type IconName } from './Icon';
import { errorMessage } from '../api';
import { STATE_ART, comingSoonCopy, errorArt } from '../lib/voice.js';
import { Fig } from './viz';

const LINE_LABEL: Record<ProductLine, string> = { fresh: 'Fresh', grill: 'Grill', mart: 'Grocery' };

/** Category chip (§14): radius 16, category fill, Ink label (B3.3). Mart reads "Grocery" (B3.10). */
export function LineChip({ line }: { line: ProductLine }) {
  return <span className={`pill chip-${line}`}>{LINE_LABEL[line]}</span>;
}

export function LineDot({ line }: { line: ProductLine }) {
  return <span className={`dot dot-${line}`} aria-hidden="true" />;
}

/** Express identity: bolt on a Pop Yellow chip (a surface, not a button: B3.1). */
export function ExpressChip({ children }: { children?: ReactNode }) {
  return (
    <span className="pill chip-express">
      <Icon name="bolt" size="sm" />
      {children ?? 'Express'}
    </span>
  );
}

export function LineChips({ lines, speed }: { lines: ProductLine[]; speed?: Speed }) {
  return (
    <div className="meta">
      {lines.map((l) => (
        <LineChip key={l} line={l} />
      ))}
      {speed === 'express' ? <ExpressChip /> : null}
    </div>
  );
}

export type Tone = 'success' | 'warning' | 'error' | 'info' | 'neutral';
export function StatusPill({ tone, children }: { tone: Tone; children: ReactNode }) {
  return <span className={`pill sticker st-${tone}`}>{children}</span>;
}

/** KPI tile (BO-13): the number (dashboard figure face, counts up) shrinks to fit its tile via
 * --kpi-chars, never truncates. */
export function Kpi({ label, value, detail }: { label: string; value: ReactNode; detail?: ReactNode }) {
  const chars = typeof value === 'string' || typeof value === 'number' ? String(value).length : 8;
  return (
    <div className="card kpi">
      <span className="label">{label}</span>
      {typeof value === 'string' || typeof value === 'number' ? (
        <Fig text={String(value)} size="lg" className="v" style={{ '--kpi-chars': Math.max(6, chars) } as CSSProperties} />
      ) : (
        <span className="v" style={{ '--kpi-chars': Math.max(6, chars) } as CSSProperties}>
          {value}
        </span>
      )}
      {detail ? <span className="d">{detail}</span> : null}
    </div>
  );
}

/** A §10 state scene from /illustrations (see STATE_ART in lib/voice.js). */
export type StateArt = (typeof STATE_ART)[keyof typeof STATE_ART];

/**
 * Empty / no-access / not-found states (BO-16). With `art`, the §10 scene leads (every scene hides
 * a pop); without it, the line icon on a Pop Yellow disc.
 */
export function EmptyState({ icon = 'check', art, title, children, action }: { icon?: IconName; art?: StateArt; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className={art ? 'empty-state has-art' : 'empty-state'}>
      {art ? <img className="state-art" src={`/illustrations/${art}.svg`} alt="" width={144} height={144} /> : <Icon name={icon} size="lg" />}
      <h2 className="t-headline">{title}</h2>
      {children ? <p>{children}</p> : null}
      {action}
    </div>
  );
}

/** Errors own it in word one and fix it in sentence two (§13); no jokes until it's fixed. */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="empty-state has-art" role="alert">
      <img className="state-art" src={`/illustrations/${errorArt(error as { status?: number; code?: string })}.svg`} alt="" width={144} height={144} />
      <h2 className="t-headline">We couldn't load this</h2>
      <p>{errorMessage(error)}</p>
      {onRetry ? (
        <button type="button" className="btn" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

/** Loading: the §12 pop loop over skeleton rows (the loop holds still under reduced motion). */
export function Loading({ label = 'Loading', rows = 1 }: { label?: string; rows?: number }) {
  return (
    <div className="loading stack" aria-busy="true" aria-label={label} role="status">
      <PopLoop />
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" />
      ))}
      <span className="sr-only">{label}…</span>
    </div>
  );
}

/** Page-level "not here yet" state for nav items that aren't built (BO-8): no phase jargon. */
export function ComingSoon({ title, scope = 'ops', children }: { title: string; scope?: 'ops' | 'shop'; children?: string }) {
  useTitle(title);
  const copy = comingSoonCopy(title, scope, children);
  return (
    <>
      <div className="top">
        <div className="grow stack s4">
          <h1 className="t-title">{title}</h1>
          <span className="t-monocap muted">{copy.caption}</span>
        </div>
      </div>
      <section className="card">
        <EmptyState art={STATE_ART.comingSoon} title={copy.heading}>
          {copy.body}
        </EmptyState>
      </section>
    </>
  );
}

/**
 * The frame for sign-in, no-access, the shop picker and 404 (BO-1): the drawn §06 lockup on the
 * field (Pop Yellow on Cart Blue in light, Cream on Ink in dark), a Plex Mono caption, then the card.
 */
export function AuthPage({ children, card = true, heading }: { children: ReactNode; card?: boolean; heading?: boolean }) {
  const brand = (
    <>
      <Lockup width={232} label="PopCart" />
      <span className="sr-only"> back office</span>
    </>
  );
  return (
    <main className="auth">
      <div className="auth-stack">
        <div className="auth-brand">
          {heading ? <h1 className="auth-lockup">{brand}</h1> : <div className="auth-lockup">{brand}</div>}
          <span className="t-monocap">Ops and Partner Hub</span>
        </div>
        {card ? <div className="card">{children}</div> : children}
      </div>
    </main>
  );
}

export function useTitle(title: string) {
  useEffect(() => {
    document.title = `${title} · PopCart`;
  }, [title]);
}

/** A ticking clock for countdowns and "updated N s ago". */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(t);
  }, [intervalMs]);
  return now;
}

/** Roving arrow-key focus for radio groups and tab lists. */
export function arrowNav(e: KeyboardEvent<HTMLElement>, selector = '[role="radio"],[role="tab"]') {
  if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) return;
  const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>(selector));
  const i = items.indexOf(document.activeElement as HTMLElement);
  if (i < 0) return;
  e.preventDefault();
  let n = i;
  if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = (i - 1 + items.length) % items.length;
  else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = (i + 1) % items.length;
  else if (e.key === 'Home') n = 0;
  else n = items.length - 1;
  items[n]?.focus();
  items[n]?.click();
}

/** Segmented control (radio group). */
export function Segmented<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: { value: T; label: string }[]; onChange(v: T): void }) {
  return (
    <div className="seg" role="radiogroup" aria-label={label} onKeyDown={(e) => arrowNav(e)}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} tabIndex={o.value === value ? 0 : -1} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Modal confirm built on <dialog> (focus trap, Esc, inert background for free). Optional
 * required reason field.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  reasonLabel,
  busy,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  reasonLabel?: string;
  busy?: boolean;
  onConfirm(reason: string): void;
  onClose(): void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      setReason('');
      setErr(null);
      d.showModal();
    } else if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className="dialog" aria-labelledby="confirm-title" onClose={onClose}>
      <form
        method="dialog"
        className="stack s24"
        onSubmit={(e) => {
          e.preventDefault();
          if (reasonLabel && reason.trim().length < 3) {
            setErr('Give a short reason (the customer sees it).');
            return;
          }
          onConfirm(reason.trim());
        }}
      >
        <h2 id="confirm-title" className="t-headline">
          {title}
        </h2>
        <div className="t-body">{body}</div>
        {reasonLabel ? (
          <div className="field">
            <label htmlFor="confirm-reason">{reasonLabel}</label>
            <textarea
              id="confirm-reason"
              className="input"
              value={reason}
              maxLength={200}
              aria-invalid={err ? true : undefined}
              aria-describedby={err ? 'confirm-err' : undefined}
              onChange={(e) => setReason(e.target.value)}
            />
            {err ? (
              <span id="confirm-err" className="pill st-error err-pill">
                {err}
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn" onClick={onClose}>
            Keep it
          </button>
          <button type="submit" className="btn btn-danger" disabled={busy} aria-busy={busy || undefined}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </form>
    </dialog>
  );
}
